import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import TrieEngine from "../AdvanceTreesEngine/TrieEngine";
import styles from "../Advancetrees.module.css";

type Operation = "insert" | "searchWord" | "searchPrefix" | "delete" | null;

const TrieWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [trieWords, setTrieWords] = useState<string[]>([]);
  const [inputWord, setInputWord] = useState<string>("");
  const [operation, setOperation] = useState<Operation>(null);
  const [highlightedNodes, setHighlightedNodes] = useState<string[]>([]);
  const [activeLine, setActiveLine] = useState<number | null>(null);
  const [resetTrigger, setResetTrigger] = useState<boolean>(false);
  const [generateTrigger, setGenerateTrigger] = useState<number>(0);

  const pseudocodeMap: Record<Exclude<Operation, null>, string[]> = {
    insert: [
      "function insert(word):",
      "    node = root",
      "    for each char in word:",
      "        if char not in node.children:",
      "            node.children[char] = new TrieNode()",
      "        node = node.children[char]",
      "    node.isEnd = true",
    ],
    searchWord: [
      "function search(word):",
      "    node = root",
      "    for each char in word:",
      "        if char not in node.children:",
      "            return false",
      "        node = node.children[char]",
      "    return node.isEnd",
    ],
    searchPrefix: [
      "function startsWith(prefix):",
      "    node = root",
      "    for each char in prefix:",
      "        if char not in node.children:",
      "            return false",
      "        node = node.children[char]",
      "    return true",
    ],
    delete: [
      "function delete(word, node, depth):",
      "    if node is null: return false",
      "    if depth == len(word):",
      "        if node.isEnd:",
      "            node.isEnd = false",
      "            return node.children is empty",
      "        return false",
      "    char = word[depth]",
      "    if delete(word, node.children[char], depth + 1):",
      "        remove char from node.children",
      "        return not node.isEnd and node.children is empty",
      "    return false",
    ],
  };
  const handleGenerateExample = () => {
  const examples = [
    ["cat", "car", "cart", "dog", "dot"],
    ["apple", "app", "apply", "ape", "apt"],
    ["bat", "ball", "barn", "bag", "bad"],
    ["code", "coder", "coding", "cold", "coin"],
    ["sun", "sand", "song", "sit", "sip"],
  ];
  const randomSet = examples[Math.floor(Math.random() * examples.length)];
  setTrieWords(randomSet);
  setOperation("insert");
  setGenerateTrigger((prev) => prev + 1);
  };

  const handleInsert = () => {
    if (!inputWord.trim()) return;
    const word = inputWord.toLowerCase();
    if (!trieWords.includes(word)) setTrieWords((prev) => [...prev, word]);
    setOperation("insert");
    setGenerateTrigger((p) => p + 1);
    setInputWord("");
  };

  const handleSearchWord = () => {
    if (!inputWord.trim()) return;
    setOperation("searchWord");
    setGenerateTrigger((p) => p + 1);
  };

  const handleSearchPrefix = () => {
    if (!inputWord.trim()) return;
    setOperation("searchPrefix");
    setGenerateTrigger((p) => p + 1);
  };

  const handleDelete = () => {
    if (!inputWord.trim()) return;
    const word = inputWord.toLowerCase();
    setTrieWords((prev) => prev.filter((w) => w !== word));
    setOperation("delete");
    setGenerateTrigger((p) => p + 1);
    setInputWord("");
  };

  const handleReset = () => {
    setInputWord("");
    setOperation(null);
    setActiveLine(null);
    setHighlightedNodes([]);
    setResetTrigger((prev) => !prev);
  };

  const pseudoLines = operation ? pseudocodeMap[operation] : null;

  const operationTitle =
    operation === null
      ? ""
      : operation === "searchWord"
      ? "Search Word"
      : operation === "searchPrefix"
      ? "Search Prefix"
      : operation.charAt(0).toUpperCase() + operation.slice(1);

  return (
    <div className={styles.advTreeContainer}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>

      <h1 className={styles.title}>Trie Visualization</h1>

      <p className={styles.description}>
        A <strong>Trie (Prefix Tree)</strong> is a specialized tree structure that stores words by their prefixes.
        Each node represents a single character, and every path from the root to a leaf forms a complete word.
        This structure allows for efficient word lookups, insertions, and prefix-based searches — making it ideal for applications like autocomplete, spell-checking, and dictionary systems.
      </p>
        <div className={styles.useCase}>
        <h3>Use Cases</h3>
            <p>
                Tries are used for <strong>Autocomplete systems, Spell checkers, Dictionary lookups,IP routing and pattern matching.</strong> 
            </p>
        </div>

        <div className={styles.timeComplexity}>
        <h3>Time Complexity</h3>
        <p>
            Inserte, search, and delete are all O(L).
        </p>
        </div>

      <div className={styles.trieWordsContainer}>
        <h3>Current Words in Trie:</h3>
        <div className={styles.trieWordsList}>
          {trieWords.length === 0 ? (
            <p className={styles.emptyText}>No words yet.</p>
          ) : (
            trieWords.map((word) => (
              <span key={word} className={styles.wordTag}>
                {word}
              </span>
            ))
          )}
        </div>
      </div>

      <div className={styles.controls}>
        <div className={styles.inputGroup}>
          <input
            type="text"
            placeholder="Enter word or prefix"
            value={inputWord}
            onChange={(e) => setInputWord(e.target.value)}
            className={styles.inputField}
          />
        <button onClick={handleInsert}>Insert Word</button>
        </div>
        <button onClick={handleGenerateExample}>Generate Example Words</button>
        <button onClick={handleSearchWord}>Search Word</button>
        <button onClick={handleSearchPrefix}>Search Prefix</button>
        <button onClick={handleDelete}>Delete Word</button>
        <button onClick={handleReset}>Reset</button>
      </div>

      <div className={styles.vizContainer}>
        <TrieEngine
        operation={operation}
        inputWord={inputWord}
        trieWords={trieWords}
        resetTrigger={resetTrigger}
        generateTrigger={generateTrigger}
        setActiveLine={setActiveLine}
        setHighlightedNodes={setHighlightedNodes}
                />
      </div>

      {pseudoLines && (
        <div className={styles.pseudoContainer}>
          <h3>Pseudocode: {operationTitle}</h3>
          <pre>
            {pseudoLines.map((line, index) => (
              <code
                key={index}
                className={activeLine === index ? styles.highlightCode : ""}
              >
                {line}
              </code>
            ))}
          </pre>
        </div>
      )}
    </div>
  );
};

export default TrieWrapper;
